// SAD-497 (SOL-01): the Pursuit's working Solution Concept. Manual by design —
// the editor does not depend on Themes, mappings, pricing, solicitation processing or AI.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { projectDataClient } from '../../../lib/connect/clients';
import { CustomerThemeEvidence } from '../CustomerThemeEvidence';
import { useCaptureWorkspace } from '../hooks';
import { Choice, Facts, options, TextField } from '../PastPerformance/Fields';
import { ReadOnlyScope } from '../record/ReadOnlyScope';
import { usePursuitCapabilities } from '../record/usePursuitCapabilities';
import { SolutionAlternatives } from './SolutionAlternatives';
import { ComponentsEditor, ComponentsReadOnly } from './SolutionComponents';
import { SolutionEvidenceReviews } from './SolutionEvidenceReviews';
import { RevisionAttribution, SolutionHandoffs } from './SolutionHandoffs';
import { ItemsEditor, ItemsReadOnly } from './SolutionItems';
import { SolutionSuggestions } from './SolutionSuggestions';
import { StaffingLoe } from './StaffingLoe';
import { StaffingReadiness } from './StaffingReadiness';
const KINDS = [
    'customer_theme',
    'request_document',
    'touchpoint',
    'sme_note',
    'prior_material',
    'pp_evidence',
    'teammate_evidence',
    'attachment'
];
const SECTIONS = [
    [
        'approach',
        'Approach',
        Testids.captureSolution.approach
    ],
    [
        'intendedOutcomes',
        'Intended outcomes',
        Testids.captureSolution.intendedOutcomes
    ],
    [
        'rationale',
        'Rationale',
        Testids.captureSolution.rationale
    ],
    [
        'constraints',
        'Constraints',
        Testids.captureSolution.constraints
    ],
    [
        'unresolvedIssues',
        'Unresolved issues',
        Testids.captureSolution.unresolvedIssues
    ]
];
const blankLink = {
    sourceKind: 'touchpoint',
    sourceLabel: '',
    sourceRef: '',
    basis: 'inferred',
    stance: 'supports',
    excerpt: ''
};
const basisLabel = (basis)=>basis === 'stated' ? 'Source states the need' : 'Team inferred a response';
function LinkRow({ link }) {
    return /*#__PURE__*/ _jsxs("li", {
        "data-testid": Testids.captureSolution.linkItem,
        className: "space-y-1 rounded-md border p-2",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 text-sm",
                children: [
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        children: link.sourceKind.replace(/_/g, ' ')
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: link.sourceLabel
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        children: basisLabel(link.basis)
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        children: link.stance
                    })
                ]
            }),
            link.sourceRef && /*#__PURE__*/ _jsxs("div", {
                className: "text-xs",
                children: [
                    "Reference: ",
                    link.sourceRef
                ]
            }),
            link.excerpt && /*#__PURE__*/ _jsx("div", {
                className: "whitespace-pre-wrap text-sm",
                children: link.excerpt
            })
        ]
    });
}
// Assumptions are rationale with no source. They are always labelled as such.
function Assumptions({ items }) {
    if (!items.length) return null;
    return /*#__PURE__*/ _jsx(Facts, {
        title: "Assumptions (unsupported — no source cited)",
        children: /*#__PURE__*/ _jsx("ul", {
            className: "list-disc pl-5",
            children: items.map((a, i)=>/*#__PURE__*/ _jsx("li", {
                    "data-testid": Testids.captureSolution.assumptionItem,
                    children: a
                }, i))
        })
    });
}
function ReadOnly({ revision, selectedOnly = false }) {
    const items = selectedOnly ? revision.items.filter((i)=>i.kind !== 'internal_assumption') : revision.items;
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.captureSolution.viewing,
        className: "space-y-3",
        children: [
            SECTIONS.map(([key, label])=>/*#__PURE__*/ _jsx(Facts, {
                    title: label,
                    children: revision[key] || 'Not recorded'
                }, key)),
            !selectedOnly && /*#__PURE__*/ _jsx(Assumptions, {
                items: revision.assumptions
            }),
            items.length > 0 && /*#__PURE__*/ _jsx(Facts, {
                title: "Typed items",
                children: /*#__PURE__*/ _jsx(ItemsReadOnly, {
                    items: items
                })
            }),
            revision.components.length > 0 && /*#__PURE__*/ _jsx(Facts, {
                title: "Components",
                children: /*#__PURE__*/ _jsx(ComponentsReadOnly, {
                    components: revision.components,
                    items: items
                })
            }),
            revision.traceLinks.length > 0 && /*#__PURE__*/ _jsx(Facts, {
                title: "Trace links",
                children: /*#__PURE__*/ _jsx("ul", {
                    className: "space-y-2",
                    children: revision.traceLinks.map((l)=>/*#__PURE__*/ _jsx(LinkRow, {
                            link: l
                        }, l.id))
                })
            })
        ]
    });
}
function Editor({ head, captureProjectId, dirtyRef }) {
    const queryClient = useQueryClient();
    const [text, setText] = useState({
        approach: head?.approach ?? '',
        intendedOutcomes: head?.intendedOutcomes ?? '',
        rationale: head?.rationale ?? '',
        constraints: head?.constraints ?? '',
        unresolvedIssues: head?.unresolvedIssues ?? ''
    });
    const [assumptions, setAssumptions] = useState(head?.assumptions ?? []);
    const [assumption, setAssumption] = useState('');
    const [items, setItems] = useState(head?.items ?? []);
    const [components, setComponents] = useState(head?.components ?? []);
    const [approachAccepted, setApproachAccepted] = useState(head?.approachAccepted ?? false);
    const [links, setLinks] = useState((head?.traceLinks ?? []).map(({ id: _id, ...l })=>({
            ...blankLink,
            ...l
        })));
    const [link, setLink] = useState(blankLink);
    const [changeNote, setChangeNote] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const patchLink = (p)=>setLink((l)=>({
                ...l,
                ...p
            }));
    const canAddLink = link.sourceLabel.trim() !== '' && (link.basis !== 'stated' || link.sourceRef.trim() !== '');
    // Tell the AI suggestion review whether Apply (which reloads this editor) would drop edits.
    useEffect(()=>{
        dirtyRef.current = SECTIONS.some(([key])=>text[key] !== (head?.[key] ?? '')) || assumptions.length !== (head?.assumptions.length ?? 0) || links.length !== (head?.traceLinks.length ?? 0) || items.length !== (head?.items.length ?? 0) || items.some((item, i)=>item !== head?.items[i]) || components.length !== (head?.components.length ?? 0) || components.some((component, i)=>component !== head?.components[i]) || approachAccepted !== (head?.approachAccepted ?? false) || changeNote !== '';
        return ()=>{
            dirtyRef.current = false;
        };
    }, [
        dirtyRef,
        head,
        text,
        assumptions,
        links,
        items,
        components,
        approachAccepted,
        changeNote
    ]);
    async function save() {
        setBusy(true);
        setError('');
        try {
            await projectDataClient.saveCaptureSolutionRevision({
                captureProjectId,
                requestId: crypto.randomUUID(),
                expectedRevision: head?.revision ?? BigInt(0),
                content: {
                    ...text,
                    assumptions,
                    changeNote,
                    traceLinks: links,
                    items,
                    components,
                    approachAccepted
                }
            });
            setChangeNote('');
            await queryClient.invalidateQueries({
                queryKey: [
                    'capture-solution',
                    captureProjectId
                ]
            });
        } catch (e) {
            const ce = ConnectError.from(e);
            setError(ce.code === Code.Aborted ? 'Someone saved a newer revision. Reload the working version and reapply your edit.' : ce.rawMessage);
        } finally{
            setBusy(false);
        }
    }
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.captureSolutionReview.editor,
        className: "space-y-3",
        children: [
            SECTIONS.map(([key, label, testId])=>/*#__PURE__*/ _jsx("div", {
                    tabIndex: -1,
                    "data-testid": Testids.captureSolutionReview.affected,
                    "data-target-id": "concept",
                    "data-target-field": key.replace(/[A-Z]/g, (v)=>`_${v.toLowerCase()}`),
                    children: /*#__PURE__*/ _jsx(TextField, {
                        label: label,
                        multiline: true,
                        testId: testId,
                        value: text[key],
                        onChange: (v)=>setText((t)=>({
                                    ...t,
                                    [key]: v
                                }))
                    })
                }, key)),
            /*#__PURE__*/ _jsxs("section", {
                tabIndex: -1,
                "data-testid": Testids.captureSolutionReview.affected,
                "data-target-id": "concept",
                "data-target-field": "assumptions",
                className: "space-y-2 rounded-md border p-3",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "font-semibold",
                        children: "Assumptions"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm",
                        children: "Rationale you cannot source. It is shown as an assumption, never as a citation."
                    }),
                    /*#__PURE__*/ _jsx(Assumptions, {
                        items: assumptions
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "New assumption",
                        testId: Testids.captureSolution.assumptionInput,
                        value: assumption,
                        onChange: setAssumption
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.captureSolution.assumptionAdd,
                        disabled: !assumption.trim(),
                        onClick: ()=>{
                            setAssumptions((a)=>[
                                    ...a,
                                    assumption.trim()
                                ]);
                            setAssumption('');
                        },
                        children: "Add assumption"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ItemsEditor, {
                items: items,
                priorIds: new Set((head?.items ?? []).map((i)=>i.id)),
                onChange: setItems,
                approachAccepted: approachAccepted,
                onAcceptedChange: setApproachAccepted
            }),
            /*#__PURE__*/ _jsx(ComponentsEditor, {
                components: components,
                items: items,
                onChange: setComponents
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "space-y-2 rounded-md border p-3",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "font-semibold",
                        children: "Trace links"
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-2",
                        children: links.map((l, i)=>/*#__PURE__*/ _jsx(LinkRow, {
                                link: l
                            }, i))
                    }),
                    /*#__PURE__*/ _jsx(Choice, {
                        label: "Source kind",
                        testId: Testids.captureSolution.linkKind,
                        value: link.sourceKind,
                        options: options(KINDS),
                        onChange: (sourceKind)=>patchLink({
                                sourceKind
                            })
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Source label",
                        testId: Testids.captureSolution.linkLabel,
                        value: link.sourceLabel,
                        onChange: (sourceLabel)=>patchLink({
                                sourceLabel
                            })
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Source reference",
                        hint: "Needed when the source states the need.",
                        testId: Testids.captureSolution.linkRef,
                        value: link.sourceRef,
                        onChange: (sourceRef)=>patchLink({
                                sourceRef
                            })
                    }),
                    /*#__PURE__*/ _jsx(Choice, {
                        label: "Basis",
                        testId: Testids.captureSolution.linkBasis,
                        value: link.basis,
                        options: [
                            {
                                value: 'stated',
                                label: basisLabel('stated')
                            },
                            {
                                value: 'inferred',
                                label: basisLabel('inferred')
                            }
                        ],
                        onChange: (basis)=>patchLink({
                                basis
                            })
                    }),
                    /*#__PURE__*/ _jsx(Choice, {
                        label: "Evidence",
                        testId: Testids.captureSolution.linkStance,
                        value: link.stance,
                        options: options([
                            'supports',
                            'contradicts'
                        ]),
                        onChange: (stance)=>patchLink({
                                stance
                            })
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Excerpt or note",
                        multiline: true,
                        testId: Testids.captureSolution.linkExcerpt,
                        value: link.excerpt,
                        onChange: (excerpt)=>patchLink({
                                excerpt
                            })
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.captureSolution.linkAdd,
                        disabled: !canAddLink,
                        onClick: ()=>{
                            setLinks((ls)=>[
                                    ...ls,
                                    link
                                ]);
                            setLink(blankLink);
                        },
                        children: "Add trace link"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "What changed (optional)",
                testId: Testids.captureSolution.changeNote,
                value: changeNote,
                onChange: setChangeNote
            }),
            error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                "data-testid": Testids.captureSolution.error,
                children: error
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.captureSolution.save,
                disabled: busy || !Object.values(text).some((v)=>v.trim()),
                onClick: save,
                children: "Save new revision"
            })
        ]
    });
}
export function SolutionTab({ captureProjectId, author, pinnedRevisionId, pinnedTargetId, readOnly = false }) {
    const pinRoot = useRef(null);
    const queryClient = useQueryClient();
    const workspace = useCaptureWorkspace(captureProjectId);
    const zouProjectId = workspace.data?.summary?.zouProjectId;
    // GRW-30: the one capability gate (header capabilities in the record shell).
    const { canEdit } = usePursuitCapabilities({
        captureProjectId,
        zouProjectId
    });
    const [viewing, setViewing] = useState();
    const [error, setError] = useState('');
    const dirtyRef = useRef(false);
    const solution = useQuery({
        queryKey: [
            'capture-solution',
            captureProjectId
        ],
        queryFn: async ()=>(await projectDataClient.getCaptureSolution({
                captureProjectId
            })).revisions,
        retry: false
    });
    const revisions = solution.data ?? [];
    const head = revisions[0];
    const viewed = pinnedRevisionId ? revisions.find((r)=>r.id === pinnedRevisionId) : revisions.find((r)=>r.revision === viewing);
    useEffect(()=>{
        if (!pinnedRevisionId || !viewed) return;
        const row = Array.from(pinRoot.current?.querySelectorAll('[data-target-id]') ?? []).find((element)=>element.dataset.targetId === pinnedTargetId) ?? pinRoot.current;
        row?.focus({
            preventScroll: true
        });
        row?.scrollIntoView({
            block: 'center'
        });
    }, [
        pinnedRevisionId,
        pinnedTargetId,
        viewed
    ]);
    async function restore(source) {
        setError('');
        try {
            await projectDataClient.restoreCaptureSolutionRevision({
                captureProjectId,
                requestId: crypto.randomUUID(),
                expectedRevision: head?.revision ?? BigInt(0),
                sourceRevision: source.revision
            });
            setViewing(undefined);
            await queryClient.invalidateQueries({
                queryKey: [
                    'capture-solution',
                    captureProjectId
                ]
            });
        } catch (e) {
            setError(ConnectError.from(e).rawMessage);
        }
    }
    if (solution.isLoading) return /*#__PURE__*/ _jsx("p", {
        children: "Loading Solution…"
    });
    if (solution.isError) return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("p", {
                role: "alert",
                children: [
                    "Could not load solution history: ",
                    ConnectError.from(solution.error).rawMessage
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>solution.refetch(),
                children: "Retry loading solution"
            })
        ]
    });
    if (pinnedRevisionId && !viewed) return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.captureSolution.root,
        children: /*#__PURE__*/ _jsx("p", {
            role: "alert",
            children: "The linked Solution revision is unavailable."
        })
    });
    return /*#__PURE__*/ _jsx(ReadOnlyScope, {
        readOnly: readOnly,
        children: /*#__PURE__*/ _jsxs("div", {
            ref: pinRoot,
            tabIndex: -1,
            "data-testid": Testids.captureSolution.root,
            "data-result-version-id": viewed?.id,
            className: "space-y-4",
            children: [
                !viewed && /*#__PURE__*/ _jsx(CustomerThemeEvidence, {
                    receiver: "solution",
                    objectId: captureProjectId
                }),
                /*#__PURE__*/ _jsx("p", {
                    "data-testid": Testids.captureSolution.notRequirement,
                    className: "text-sm",
                    children: "Working Solution Concept: the team's own design. Nothing here is a government requirement unless a trace link says the source states it."
                }),
                /*#__PURE__*/ _jsx(SolutionEvidenceReviews, {
                    captureProjectId: captureProjectId,
                    head: head,
                    onReturn: (review)=>{
                        setViewing(undefined);
                        requestAnimationFrame(()=>{
                            const editor = document.querySelector(`[data-testid="${Testids.captureSolutionReview.editor}"]`);
                            const target = Array.from(editor?.querySelectorAll(`[data-testid="${Testids.captureSolutionReview.affected}"]`) ?? []).find((element)=>element.dataset.targetId === review.targetId && (review.targetKind !== 'concept' || element.dataset.targetField === review.targetField));
                            target?.focus();
                            target?.scrollIntoView({
                                block: 'center'
                            });
                        });
                    }
                }),
                viewed && /*#__PURE__*/ _jsxs("section", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsxs("h3", {
                            className: "font-semibold",
                            children: [
                                "Revision ",
                                String(viewed.revision),
                                " (read-only)"
                            ]
                        }),
                        /*#__PURE__*/ _jsx(ReadOnly, {
                            revision: viewed
                        }),
                        pinnedRevisionId ? /*#__PURE__*/ _jsx(Link, {
                            to: `/capture/${captureProjectId}?tab=solution`,
                            children: "Back to working version"
                        }) : /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.captureSolution.closeViewing,
                            onClick: ()=>setViewing(undefined),
                            children: "Back to working version"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    hidden: !!viewed,
                    children: [
                        /*#__PURE__*/ _jsx("h3", {
                            "data-testid": Testids.captureSolution.headRevision,
                            className: "font-semibold",
                            children: head ? `Working version: revision ${String(head.revision)}` : 'No revision yet'
                        }),
                        /*#__PURE__*/ _jsx(Editor, {
                            head: head,
                            captureProjectId: captureProjectId,
                            dirtyRef: dirtyRef
                        }, head?.id ?? 'new')
                    ]
                }),
                /*#__PURE__*/ _jsx(SolutionSuggestions, {
                    captureProjectId: captureProjectId,
                    head: head,
                    dirtyRef: dirtyRef
                }),
                /*#__PURE__*/ _jsx(SolutionHandoffs, {
                    captureProjectId: captureProjectId,
                    revisions: revisions,
                    author: author,
                    renderSolution: (revision)=>/*#__PURE__*/ _jsx(ReadOnly, {
                            revision: revision,
                            selectedOnly: true
                        })
                }),
                /*#__PURE__*/ _jsx(SolutionAlternatives, {
                    captureProjectId: captureProjectId
                }),
                /*#__PURE__*/ _jsx(StaffingLoe, {
                    captureProjectId: captureProjectId
                }),
                /*#__PURE__*/ _jsx(StaffingReadiness, {
                    captureProjectId: captureProjectId,
                    readOnly: readOnly || workspace.isError || !zouProjectId || !canEdit
                }),
                error && /*#__PURE__*/ _jsx("p", {
                    role: "alert",
                    children: error
                }),
                /*#__PURE__*/ _jsxs("section", {
                    className: "space-y-2 rounded-md border p-3",
                    children: [
                        /*#__PURE__*/ _jsx("h3", {
                            className: "font-semibold",
                            children: "Revision history"
                        }),
                        /*#__PURE__*/ _jsx("ul", {
                            className: "space-y-2",
                            children: revisions.map((r)=>/*#__PURE__*/ _jsxs("li", {
                                    "data-testid": Testids.captureSolution.historyRow,
                                    className: "flex flex-wrap items-center gap-2 text-sm",
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "font-medium",
                                            children: [
                                                "Revision ",
                                                String(r.revision)
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(RevisionAttribution, {
                                            revision: r,
                                            author: author
                                        }),
                                        r.appliedSuggestionId && /*#__PURE__*/ _jsx(SpecraBadge, {
                                            "data-testid": Testids.captureSolution.historyAiApplied,
                                            children: "Applied AI suggestion"
                                        }),
                                        r.restoredFromRevision !== undefined && /*#__PURE__*/ _jsxs(SpecraBadge, {
                                            children: [
                                                "restored from ",
                                                String(r.restoredFromRevision)
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            "data-testid": Testids.captureSolution.viewRevision,
                                            onClick: ()=>setViewing(r.revision),
                                            children: "View"
                                        }),
                                        r.id !== head?.id && /*#__PURE__*/ _jsx(SpecraButton, {
                                            "data-testid": Testids.captureSolution.restore,
                                            onClick: ()=>restore(r),
                                            children: "Restore as new revision"
                                        })
                                    ]
                                }, r.id))
                        })
                    ]
                })
            ]
        })
    });
}
